import React, { useEffect, useState } from 'react'
import {
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress // Import CircularProgress
} from '@mui/material'

import PictureAsPdfSharpIcon from '@mui/icons-material/PictureAsPdfSharp'
import DescriptionIcon from '@mui/icons-material/Description' // JSON viewer icon
import { useSelector } from 'react-redux'
import CloseIcon from '@mui/icons-material/Close'
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined'
import DOMPurify from 'dompurify'
import PdfJsonViewer from '../PDFViewer/PDFViewerNew'
import ReactJson from 'react-json-view' // For pretty printing JSON
import { useDispatch } from 'react-redux'
import fetchData from '../../utils/fetchData'
import { amber } from '@mui/material/colors' // Import amber color

const DocumentSummary = () => {
  const [value, setValue] = useState(0)
  const [openDialog, setOpenDialog] = useState(false)
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [openJsonDialog, setOpenJsonDialog] = useState(false) // State for JSON dialog
  const [currentPdfLink, setCurrentPdfLink] = useState('')
  const [pdfName, setPdfName] = useState('')
  const [attachmentJsonData, setAttachmentJsonData] = useState({})
  const dispatch = useDispatch()
  const selectedApplicationData = useSelector(
    state => state.intakeData.selectedApplicationData
  )

  const [documentSummaryData, setDocumentSummaryData] = useState([])
  const [loading, setLoading] = useState(true) // Add a loading state

  //  let documentSummaryData

  useEffect(() => {
    if (selectedApplicationData?.application_id) {
      setLoading(true) // Start loading before fetching data

      fetchData(
        `https://insureai-intake-dev-1074099157700.us-central1.run.app/insureai/ml/intake/applications/v2/attachments?application_id=${selectedApplicationData?.application_id}`,
        'GET'
      )
        .then(data => {
          setDocumentSummaryData(data)
        })
        .catch(error => {
          console.error('Error fetching document summary data:', error)
        })
        .finally(() => {
          setLoading(false) // Stop loading regardless of success or failure
        })
    } else {
      setDocumentSummaryData([]) // Reset data when application_id is not available.
      setLoading(false) // Ensure loading is false if no data is fetched
    }
  }, [selectedApplicationData])

  const handleEditButton = () => {
    setOpenEditDialog(true)
    setOpenDialog(false)
  }

  const handlePdfClick = async (pdfLink, pdfName) => {
    setCurrentPdfLink(pdfLink)
    setOpenDialog(true) // Open dialog after setting the link
    setPdfName(pdfName)
    setAttachmentJsonData({})
  }

  const handleJsonClick = (docId, pdfName) => {
    setOpenJsonDialog(true)
    setPdfName(pdfName)
  }

  const handleDialogClose = () => {
    setOpenDialog(false)
  }

  const handleEditDialogClose = () => {
    setOpenEditDialog(false)
  }

  const handleJsonDialogClose = () => {
    // Function to close JSON dialog
    setOpenJsonDialog(false)
  }

  const closeDialog = () => {
    setOpenDialog(false)
    setOpenEditDialog(false)
    setOpenJsonDialog(false) // Close JSON dialog as well
  }

  const handleTabChange = (event, newValue) => {
    setValue(newValue)
    fetchData(
      `https://insureai-intake-dev-1074099157700.us-central1.run.app/insureai/ml/intake/applications/v2/detailed_json?doc_id=${documentSummaryData[newValue]?.doc_id}`
    )
      .then(data => {
        setAttachmentJsonData(data?.[0]?.json)
      })
      .catch(error => {
        console.error('Error fetching document summary data:', error)
      })
  }

  useEffect(() => {
    setValue(0)
  }, [selectedApplicationData])

  return (
    <>
      {loading ? ( // Conditional rendering based on loading state
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '400px' // Adjust height as needed
          }}
        >
          <CircularProgress sx={{ color: '#ea580c' }} /> {/* Orange loader */}
        </Box>
      ) : (
        documentSummaryData && (
          <div className='document-summary mt-4'>
            <h1 className='mb-2 text-2xl'>Document Summary</h1>
            {/* Horizontal scrollable tabs */}
            <Box sx={{ borderColor: 'divider', overflowX: 'auto' }}>
              <Tabs
                value={value}
                onChange={handleTabChange}
                variant='scrollable'
                scrollButtons='auto'
                aria-label='document summary tabs'
                className='12'
              >
                {documentSummaryData?.map((attachment, index) => (
                  <Tab
                    key={index}
                    label={attachment.name}
                    className='tab-view'
                    iconPosition='start'
                    sx={{ minWidth: '175px' }} // Ensures consistent size for tabs
                  />
                ))}
              </Tabs>
            </Box>

            {/* Content for the selected tab */}
            <Box className='p-6 doc-content'>
              <div className='flex justify-between items-center'>
                <h2 className='font-semibold mb-2'>
                  Summary
                  <PictureAsPdfSharpIcon
                    className='cursor-pointer text-orange-600 ml-2'
                    onClick={() =>
                      handlePdfClick(
                        documentSummaryData[value]?.link,
                        documentSummaryData[value]?.name
                      )
                    }
                  />
                  <DescriptionIcon
                    onClick={() =>
                      handleJsonClick(
                        documentSummaryData[value]?.doc_id,
                        documentSummaryData[value]?.name
                      )
                    }
                    className='cursor-pointer text-orange-600 ml-2'
                  />
                </h2>
              </div>
              {/* Display the summary for the selected tab */}
              <p
                className='text-gray-700 document-summary'
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    (documentSummaryData &&
                      documentSummaryData[value]?.summary) ||
                      ''
                  )
                }}
              />
            </Box>

            {/* PDF Viewer Dialog */}
            <Dialog
              open={openDialog}
              onClose={handleDialogClose}
              maxWidth='lg'
              fullWidth
              aria-labelledby='pdf-viewer-title'
            >
              <DialogTitle id='pdf-viewer-title'>
                {pdfName}
                <span onClick={handleDialogClose} className='close-icon'>
                  <CloseIcon />
                </span>
              </DialogTitle>
              <DialogContent>
                {documentSummaryData[value]?.is_application && (
                  <button className='edit-pdf-btn' onClick={handleEditButton}>
                    {' '}
                    <EditNoteOutlinedIcon /> Edit PDF
                  </button>
                )}
                <Box sx={{ height: '70vh' }}>
                  <iframe
                    src={currentPdfLink}
                    width='100%'
                    height='100%'
                    title='PDF Viewer'
                    style={{ border: 'none' }}
                  />
                </Box>
              </DialogContent>
            </Dialog>

            {/* JSON Viewer Dialog */}
            <Dialog
              open={openJsonDialog}
              onClose={handleJsonDialogClose}
              maxWidth='lg'
              fullWidth
              aria-labelledby='json-viewer-title'
            >
              <DialogTitle id='json-viewer-title'>
                {pdfName} JSON Data
                <span onClick={handleJsonDialogClose} className='close-icon'>
                  <CloseIcon />
                </span>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ height: '65vh', overflowY: 'auto' }}>
                  <ReactJson
                    src={attachmentJsonData || {}}
                    theme='monokai'
                    style={{ fontSize: '14px' }}
                    displayObjectSize={false}
                    displayDataTypes={false}
                  />
                </Box>
              </DialogContent>
            </Dialog>

            {/* Edit PDF Dialog */}
            <Dialog
              open={openEditDialog}
              onClose={handleEditDialogClose}
              maxWidth='lg'
              fullWidth
              aria-labelledby='pdf-viewer-title'
            >
              <DialogTitle id='pdf-viewer-title'>
                {pdfName}{' '}
                <span onClick={handleEditDialogClose} className='close-icon'>
                  <CloseIcon />
                </span>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ height: '65vh' }}>
                  <PdfJsonViewer
                    docId={documentSummaryData[value]?.doc_id}
                    applicationId={selectedApplicationData?.application_id}
                    currentPdfLink={currentPdfLink}
                    closeDialog={closeDialog}
                  />
                </Box>
              </DialogContent>
            </Dialog>
          </div>
        )
      )}
    </>
  )
}

export default DocumentSummary
